import addCalendarEventsToCache from './addCalendarEventsToCache';
import CalendarFolderItemIdIndex from '../CalendarFolderItemIdIndex';
import clearCalendarEventsInCache from './clearCalendarEventsInCache';
import datapoints from '../../datapoints';
import DateRange from '../../store/schema/DateRange';
import getCalendarEventsFromServer from '../../services/getCalendarEventsFromServer';
import markerKeys from '../../utils/markerKeys';
import { areDateRangesEqual, containsDateRange } from 'mini-datetime-utils';
import { CalendarEvent } from 'mini-calendar-event-types';
import { convertDateRangeToDisplayDateRange } from '../../utils/epochTimeToDisplayDate';
import { getEventsFromSessionStorage } from 'mini-calendar-events-session-storage';
import { logPerfMeasure } from 'bootstrap/lib/logPerfMeasure';
import { safePerformanceMarkEnd, safePerformanceMarkStart } from 'mini-performance';
import { trace } from 'owa-trace';
import { traceAndReject } from 'error-handling';
import {
    getCalendarInfoStore,
    getDateRangeStore,
    getCalendarFolderItemIdIndex,
} from '../../store/store';
import { getTimeFromFetchStart } from 'mini-performance/lib/utils/getTimeFromFetchStart';

const assign = require('object-assign');

const ALL_FOLDERS = 'all_folders';

let eventsToAdd = [];

interface ReloadCalendarEventsInfo {
    dateRange: DateRange;
    reloadPromise: Promise<void>;
}

interface ActiveReloadRequestInfo {
    index: number;
    info: ReloadCalendarEventsInfo;
}

const reloadCalendarEventsInfoMap: { [folderId: string]: ReloadCalendarEventsInfo[] } = {};

function reloadRequestStarted(
    inputFolderId: string,
    dateRange: DateRange,
    reloadPromise: Promise<void>
) {
    const folderId = inputFolderId || ALL_FOLDERS;
    reloadCalendarEventsInfoMap[folderId] = reloadCalendarEventsInfoMap[folderId] || [];
    reloadCalendarEventsInfoMap[folderId].push({
        dateRange: dateRange,
        reloadPromise: reloadPromise,
    });
}

function reloadRequestEnded(inputFolderId: string, dateRange: DateRange) {
    const folderId = inputFolderId || ALL_FOLDERS;
    const activeNetworkRequest = getActiveReloadRequestForDateRange(
        folderId,
        dateRange,
        true /* exact match */
    );
    if (activeNetworkRequest && activeNetworkRequest.index !== -1) {
        reloadCalendarEventsInfoMap[folderId].splice(activeNetworkRequest.index, 1);
    }
}

// Private function for comparing two date ranges.
// This is not comparison of OwaDateRange and hence not part of mini-datetime-utils
function areTwoDateRangesSame(dateRange1: DateRange, dateRange2: DateRange, exactMatch?: boolean) {
    const d1 = convertDateRangeToDisplayDateRange(dateRange1);
    const d2 = convertDateRangeToDisplayDateRange(dateRange2);
    return exactMatch
        ? areDateRangesEqual(d1, d2)
        : containsDateRange(d1, d2, true /* inclusive */);
}

export function getActiveReloadRequestForDateRange(
    inputFolderId: string,
    requestedDateRange: DateRange,
    getExactMatch: boolean = false
): ActiveReloadRequestInfo {
    const folderId = inputFolderId || ALL_FOLDERS;
    const infoList = reloadCalendarEventsInfoMap[folderId] || [];
    for (let i = 0; i < infoList.length; i++) {
        const isMatch = areTwoDateRangesSame(
            infoList[i].dateRange,
            requestedDateRange,
            getExactMatch
        );

        if (isMatch) {
            return { index: i, info: infoList[i] };
        }
    }
    return null;
}

/**
 * Clear the existing cache and loads the events for the given date range from the server.
 * @param dateRange date range
 * @param folderId folderId of the calendar
 * @param ctx app context
 * @param fromSessionStorage flag to check if the reload must be from session storage
 */
export default function reloadCalendarEventsInCache(
    dateRange: DateRange,
    folderId?: string,
    ctx?: any,
    fromSessionStorage: boolean = false
): Promise<void> {
    ctx?.signal(datapoints.DAL.RCE.REQUEST, {
        folderId: folderId,
        fromSessionStorage: fromSessionStorage,
        ...getStartAndEndValues(dateRange),
    });

    let resultOfNetworkFetch = true;
    const currentDALDateRange = getDateRangeStore().getDateRange();

    if (!currentDALDateRange || !areTwoDateRangesSame(currentDALDateRange, dateRange)) {
        // If date ranges are not equal, it means data for this data has not been fetched.
        getDateRangeStore().setDateRange(dateRange);
    }

    const reloadPromise = (async () => {
        try {
            if (fromSessionStorage) {
                await populateEventsFromSessionStorage(ctx);
            } else {
                resultOfNetworkFetch = await populateEventsFromNetwork(dateRange, folderId, ctx);
            }

            if (resultOfNetworkFetch) {
                // Set the events store date range to new date range
                ctx?.signal(datapoints.DAL.RCE.DATE_RANGE_SET, getStartAndEndValues(dateRange));
                getDateRangeStore().setDateRangeAlreadyFetched(dateRange);
                return Promise.resolve();
            } else {
                return Promise.reject('REJECT_AS_STALE');
            }
        } catch (error) {
            return traceAndReject('reloadCalendarEventsInCache', error);
        } finally {
            reloadRequestEnded(folderId, dateRange);
        }
    })();

    reloadRequestStarted(folderId, dateRange, reloadPromise);
    return reloadPromise;
}

async function populateEventsFromSessionStorage(ctx: any) {
    // Get events from Session storage
    safePerformanceMarkStart(markerKeys.getEventsFromSessionStorage);
    const eventsFromSessionStorage = getEventsFromSessionStorage();
    ctx?.signal(datapoints.DAL.RCE.ALL_SESSION_STORAGE_EVENT_COUNT, {
        count: eventsFromSessionStorage.length,
    });

    const events = eventsFromSessionStorage.map(sessionEvent =>
        assign(new CalendarEvent(), sessionEvent)
    );

    safePerformanceMarkEnd(markerKeys.getEventsFromSessionStorage);

    // Clear the events from store.
    clearCalendarEventsInCache();

    // Add events to store.
    safePerformanceMarkStart(markerKeys.addEventsToCacheFromSessionStorage);
    addCalendarEventsToCache(events);
    safePerformanceMarkEnd(markerKeys.addEventsToCacheFromSessionStorage);
}

async function populateEventsFromNetwork(
    dateRange: DateRange,
    folderId: string,
    ctx: any,
    bufferEvents: boolean = false
): Promise<boolean> {
    const calendarInfoStore = getCalendarInfoStore();
    const calendars = folderId
        ? [calendarInfoStore.getCalendarInfo(folderId)]
        : calendarInfoStore.getAllCalendarInfo();

    const networkRequestTimeStamp = Date.now();

    // Get calendar events from  the server
    safePerformanceMarkStart(markerKeys.getCalendarViewService);
    const eventsPromises: Promise<CalendarEvent[]>[] = [];
    for (const calendar of calendars) {
        eventsPromises.push(
            getCalendarEventsFromServer(
                calendar,
                convertDateRangeToDisplayDateRange(dateRange),
                ctx
            )
        );
    }

    ctx?.signal(datapoints.DAL.RCE.CALENDAR_EVENT_REQUESTS, {
        countOfRequests: eventsPromises.length,
        ...getStartAndEndValues(dateRange),
        time: getTimeFromFetchStart(),
    });

    const events: CalendarEvent[][] = await Promise.all(eventsPromises);

    const currentDALDateRange = getDateRangeStore().getDateRange();
    // If date ranges do not match, then no point continuing further.
    // The reasons to discard responses:
    // 1. To prevent unnecessary DAL operations for stale responses
    // which might result in timestamp getting updated, and then the last response will detect that a calendar is dirty.
    // 2. To avoid unnecessary DAL additions/deletions when surface has any way moved to a new date range.

    if (!areTwoDateRangesSame(currentDALDateRange, dateRange)) {
        return false;
    }

    const calendarFolderItemIdIndex: CalendarFolderItemIdIndex = getCalendarFolderItemIdIndex();
    const gvcPerfMeasure = safePerformanceMarkEnd(markerKeys.getCalendarViewService);

    // calculate events count
    let totalEvents = 0;
    events?.forEach(events => {
        if (events) {
            totalEvents = totalEvents + events.length;
        }
    });

    logPerfMeasure(
        gvcPerfMeasure,
        undefined /** name */,
        true /** logOnce */,
        false,
        true /** markAsCore */,
        {
            ec: totalEvents,
            cc: events?.length,
        }
    );

    // The calendar info store might be in process of update if there is a rebootstrapping.
    // In that case, it is important to await till the store is ready for use.
    // However only await if it is not ready for use
    if (!getCalendarInfoStore().isCalendarInfoReadyForUse()) {
        await getCalendarInfoStore().onCalendarInfoReadyForUse();
    }
    clearCalendarEventsInCache(folderId);

    // Add events to store.
    safePerformanceMarkStart(markerKeys.addEventsToCacheFromNetwork);
    const dirtyCalendarPromises = [];
    for (let index = 0; index < calendars.length; index++) {
        // Add the events only if calendar still present in calendar info store.
        const isCalendarStillPresent = !!getCalendarInfoStore().getCalendarInfo(
            calendars[index].calculatedFolderId
        );

        if (!isCalendarStillPresent) {
            trace.info('A calendar was removed from DAL so adding events for it can be skipped.');
            continue;
        }

        const currentCalLastTimeStamp = calendarFolderItemIdIndex
            .getFolderIdLastOpTimestampMap()
            .get(calendars[index].calculatedFolderId);

        if (!currentCalLastTimeStamp || currentCalLastTimeStamp < networkRequestTimeStamp) {
            events[index] && eventsToAdd.push(...events[index]);
            ctx?.signal(datapoints.DAL.RCE.NETWORK_EVENT_COUNT, {
                count: events[index] && events[index].length,
                ...getStartAndEndValues(dateRange),
            });
        } else {
            trace.info('A calendar found dirty so will need to reload this again.');
            dirtyCalendarPromises.push(
                populateEventsFromNetwork(
                    dateRange,
                    calendars[index].calculatedFolderId,
                    ctx,
                    true /** Buffer Events  */
                )
            );
        }
    }

    if (dirtyCalendarPromises.length) {
        await Promise.all(dirtyCalendarPromises);
    }

    if (!bufferEvents) {
        addCalendarEventsToCache(eventsToAdd);
        eventsToAdd = [];
    }

    /**
     * Signal to outer world that networks requests completed so in case,
     * anything network related which has been suspended can be proceeded.
     */
    ctx?.signal(datapoints.DAL.RCE.NETWORK_CALENDAR_EVENTS_RECEIVED, {
        time: getTimeFromFetchStart(),
    });

    const aetcnPerfMeasure = safePerformanceMarkEnd(markerKeys.addEventsToCacheFromNetwork);
    logPerfMeasure(aetcnPerfMeasure, undefined /** name */, true /** logOnce */);
    return true;
}

const getStartAndEndValues = (dateRange: DateRange) => ({
    start: dateRange?.start && new Date(dateRange.start).toDateString(),
    end: dateRange?.end && new Date(dateRange.end).toDateString(),
});
